// src/components/layout/LayoutContainer.tsx
import React from "react";
import { Box, alpha, useMediaQuery, useTheme, Typography, Button, Chip } from "@mui/material";
import { useLocation } from "react-router-dom";
import ScienceIcon from "@mui/icons-material/Science";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FloatingGlassContainer from "./FloatingGlassContainer";
// import FloatingHelpAssistant from "./FloatingHelpAssistant";
import TopNavBarApp from "./TopNavBarApp";
import { OnboardingOverlay, WelcomeSetupModal } from "../onboarding";
import { LegalAcceptanceModal } from "../legal";
import {
  TutorialContext,
  useOnboardingTutorialState,
} from "hooks/useOnboardingTutorial";
import {
  LegalComplianceContext,
  useLegalComplianceState,
} from "hooks/useLegalCompliance";
import { useFeatureGate } from "hooks/useFeatureGate";
import { DASHBOARD_PATH } from "../dashboards/dashboardSurface";
import DashboardGround from "../dashboards/DashboardGround";
import useActivityTracker from "../../hooks/useActivityTracker";
import { useTranslation } from "../../i18n";

interface LayoutContainerProps {
  children: React.ReactNode;
}

export default function LayoutContainer({ children }: LayoutContainerProps) {
  useActivityTracker();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMedium = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const location = useLocation();
  const navbarHeight = theme.custom.navbarHeight;
  const tutorialState = useOnboardingTutorialState();
  const legalState = useLegalComplianceState();
  const { isSimulating, simulatedTierName, setSimulatedTier, simulatableTiers, beta, subscriptionStatus, tier } = useFeatureGate();
  const { t } = useTranslation();
  // ----------------------------
  // Layout control arrays
  // ----------------------------
  // Paths where NO navbar should be shown
  const navbarExcludedPaths = [
    "/login",
  ];
  // (appNavbarPaths removed — TopNavBarApp is now the only navbar for all non-excluded paths)

  // All non-glass paths use transparent background (gradient shows through)
  const whiteBackgroundExcludedPaths = [
    "/inexisting_url",
    "/landing",
    "/login",
    "/linkedin",
    "/microsoft",
    "/not-found",
  ];
  const glassFloatingPaths = ["/your-floaty-page"];
  // Pages that use fixed glass container (desktop-app feel)
  const fixedGlassContainerPaths = [
    "/dashboard",
    "/patients/:id/details",
    "/billing",
    "/environments",
    "/superadmin",
    "/bulk-upload/results/:jobId",
  ];

  // ----------------------------
  // Derived state
  // ----------------------------
  const isNavbarExcluded = navbarExcludedPaths.some((path) =>
    location.pathname === path || location.pathname.startsWith(path + "/")
  );
  const isWhiteBackgroundExcluded = whiteBackgroundExcludedPaths.some((path) =>
    location.pathname === path || location.pathname.startsWith(path + "/")
  );
  const isGlassFloatingPath = glassFloatingPaths.some((path) =>
    new RegExp(`^${path.replace(/:\w+/g, "[^/]+")}$`).test(location.pathname)
  );

  const isFixedGlassContainer = fixedGlassContainerPaths.some((path) =>
    new RegExp(`^${path.replace(/:\w+/g, "[^/]+")}$`).test(location.pathname)
  );
  // Patient details: 90%, Dashboard & Billing: 75%
  const isPatientDetails = /^\/patients\/[^/]+\/details$/.test(
    location.pathname
  );
  // Pages that render their OWN VideoGlassContainer chrome. Two consequences below:
  //   • width — the outer 1200px cap squeezed a 6-column table of 70+ rows until
  //     "Billed to" clipped, so these get the uncapped patient-details treatment;
  //   • chrome — the outer glass is made INVISIBLE (transparent bg, no border, no
  //     shadow, no blur) while remaining in the layout tree, so the responsive
  //     width ladder, flex sizing and scroll math are all untouched. Two stacked
  //     glass surfaces at nearly the same size read as a misaligned artifact, not
  //     a frame: a thin uneven halo with two mismatched radii. This mirrors the
  //     recipe already written for `data-empty-dashboard` below.
  // `/bulk-upload/results/:jobId` is matched by PREFIX: it is a dynamic route,
  // so an exact-match list silently misses it — and it is the page this whole
  // aesthetic was lifted from, so it was the one showing the halo.
  // /dashboard joins this set: it supplies its own chrome AND needs the
  // uncapped width. At maxWidth 1200 on a 1920 viewport, 37.5% of the screen
  // goes unused while four KPI cards squeeze into ~275px each and the two
  // chart columns into ~570px. That is the same squeeze that forced /billing
  // and /superadmin off the cap.
  const isPatientDetailsPath = /^\/patients\/[^/]+\/details$/.test(
    location.pathname,
  );

  const pageSuppliesOwnChrome =
    ["/environments", "/billing", "/superadmin"].includes(location.pathname) ||
    location.pathname.startsWith("/bulk-upload/results/") ||
    location.pathname === DASHBOARD_PATH;

  // The dashboard has no outer container: its sections carry their own glass,
  // so the GROUND has to be dark. The panels are alpha(deepNavy, 0.35), which
  // over the pastel body gradient washes out to a mauve barely a shade off the
  // page and takes the white ink down with it. Painted here, as a fixed layer
  // behind the whole viewport, because the content lives in a
  // `position: fixed` box: a background on the flow parent would not reach
  // under it.
  // The proposal asked for the dashboard's own ground here too, not the pastel
  // gradient: the rail and the glass container both read against it the way
  // the dashboard's panels do.
  const isDashboardOpen =
    location.pathname === DASHBOARD_PATH || isPatientDetailsPath;
  // Patient Details container width (uncapped — no maxWidth). Single knob to
  // widen the whole patient page; was inline "90%".
  // 98%, not 90%: at 90% on a 1920 screen ~190px of gutter sat unused on
  // either side of the patient page.
  const PATIENT_DETAILS_WIDTH = "98%";

  // Vertical breathing room above/below the floating container card. Trimmed
  // 16→12 to give every page (especially small laptop screens) a little more
  // height without crowding the navbar; the layout still floats off the edges.
  const yMargin = 12;

  return (
    <LegalComplianceContext.Provider value={legalState}>
    <TutorialContext.Provider value={tutorialState}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflow: isFixedGlassContainer ? "hidden" : undefined,
      }}
    >
      {isDashboardOpen && <DashboardGround />}

      {/* 🔹 Conditional NavBar — always TopNavBarApp unless excluded */}
      {!isNavbarExcluded && <TopNavBarApp />}

      {/* 🔹 Beta Tier Simulation Banner */}
      {isSimulating && (
        <Box
          sx={{
            position: "fixed",
            top: navbarHeight,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar - 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 0.5,
            px: 2,
            background: `linear-gradient(90deg, ${theme.palette.warning.dark}, ${theme.palette.warning.main})`,
            color: theme.palette.warning.contrastText,
            boxShadow: `0 2px 8px ${alpha(theme.palette.warning.dark, 0.3)}`,
          }}
        >
          <ScienceIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            {t("tier_sim.simulating", "SIMULATING:")} {simulatableTiers.find(st => st.name === simulatedTierName)?.display_name ?? simulatedTierName} {t("tier_sim.tier_suffix", "tier")}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
            {simulatableTiers.map((st) => (
              <Chip
                key={st.name}
                label={st.display_name}
                size="small"
                variant={simulatedTierName === st.name ? "filled" : "outlined"}
                onClick={() => setSimulatedTier(st.name === simulatedTierName ? null : st.name)}
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  color: simulatedTierName === st.name ? theme.palette.common.white : "inherit",
                  borderColor: alpha(theme.palette.common.white, 0.5),
                  backgroundColor: simulatedTierName === st.name
                    ? alpha(theme.palette.common.white, 0.25)
                    : "transparent",
                  "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.15) },
                }}
              />
            ))}
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={() => setSimulatedTier(null)}
            sx={{
              color: "inherit",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.7rem",
              ml: 1,
              "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.15) },
            }}
          >
            {t("tier_sim.stop", "Stop")}
          </Button>
        </Box>
      )}

      {/* 🔹 Downgrade / Expiry Banner */}
      {(() => {
        const isBetaExpired = beta?.is_beta_tester === false && !beta?.is_founder && beta?.user_in_beta === false && tier?.name === "free" && subscriptionStatus === "canceled";
        const isBetaTesterExpiringSoon = beta?.is_beta_tester && beta?.beta_expires_at && new Date(beta.beta_expires_at) <= new Date(Date.now() + 7 * 86400000);
        const isPastDue = subscriptionStatus === "past_due";

        const bannerMsg = isPastDue
          ? t("billing.banner.past_due", "Payment failed. Please update your payment method to avoid service interruption.")
          : isBetaExpired
            ? t("billing.banner.beta_expired", "Your beta access has ended. Subscribe to a plan to continue using all features.")
            : isBetaTesterExpiringSoon
              ? t("billing.banner.beta_expiring", "Your beta access expires soon. Subscribe to keep your current features.")
              : null;

        if (!bannerMsg) return null;

        return (
          <Box
            sx={{
              position: "fixed",
              top: navbarHeight + (isSimulating ? 32 : 0),
              left: 0,
              right: 0,
              zIndex: theme.zIndex.appBar - 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 0.75,
              px: 2,
              background: isPastDue
                ? `linear-gradient(90deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`
                : `linear-gradient(90deg, ${theme.palette.warning.dark}, ${theme.palette.warning.main})`,
              color: theme.palette.common.white,
              boxShadow: `0 2px 8px ${alpha(theme.palette.error.dark, 0.3)}`,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
              {bannerMsg}
            </Typography>
            <Button
              size="small"
              variant="text"
              href="/billing"
              sx={{
                color: "inherit",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.7rem",
                ml: 1,
                textDecoration: "underline",
                "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.15) },
              }}
            >
              {isPastDue
                ? t("billing.banner.update_payment", "Update Payment")
                : t("billing.banner.upgrade", "Upgrade Now")}
            </Button>
          </Box>
        );
      })()}

      {/* 🔹 Main Content Area */}
      {isFixedGlassContainer ? (
        // ✅ Fixed glass container (Dashboard & Patient Details): desktop-app feel
        <Box
          sx={{
            position: "fixed",
            // v2 patient details: NO inset. Every other page here is a card
            // floating off the edges, so the box is pushed in by yMargin top
            // and bottom — which is exactly why the docked rail stopped 24px
            // short of the viewport at BOTH ends and left a strip of ground
            // under it. A column docked to the edge has to own the full height.
            top: isMobile ? navbarHeight + yMargin : navbarHeight + 2 * yMargin,
            bottom: isMobile ? yMargin : 2 * yMargin,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: isMobile ? 1 : 3,
            gap: 1,
          }}
        >
          {/* Joan, 2026-05-27: floating header slot. The dashboard portals
              search + back-arrow + FAB into this Box so they sit ABOVE the
              glass container, free of its chrome. Width matches the glass
              container for column alignment. */}
          <Box
            id="dashboard-floating-header"
            sx={{
              width: isMobile
                ? "100%"
                : isPatientDetails || pageSuppliesOwnChrome
                ? PATIENT_DETAILS_WIDTH
                : isMedium
                ? "90%"
                : "75%",
              maxWidth: isPatientDetails || pageSuppliesOwnChrome ? undefined : 1200,
              flexShrink: 0,
              "&:empty": { display: "none" },
            }}
          />
          <Box
            data-glass-container
            sx={{
              // Patient Details: wider, Dashboard: responsive width
              // Mobile: full width, Medium (md-lg): 90%, Large (lg+): 75% capped at 1200px
              width: isMobile
                ? "100%"
                : isPatientDetails || pageSuppliesOwnChrome
                ? PATIENT_DETAILS_WIDTH
                : isMedium
                ? "90%"
                : "75%",
              maxWidth: isPatientDetails || pageSuppliesOwnChrome ? undefined : 1200,
              flex: 1,
              minHeight: 0,
              // Glass container styling - using theme container opacity
              background: pageSuppliesOwnChrome
                ? "transparent"
                : alpha(
                    theme.palette.secondary.main,
                    theme.custom?.containerOpacity?.medium || 0.75
                  ),
              backdropFilter: pageSuppliesOwnChrome ? "none" : "blur(12px)",
              borderRadius: isMobile ? 2 : 3,
              border: `1px solid ${
                pageSuppliesOwnChrome
                  ? "transparent"
                  : alpha(theme.palette.common.white, 0.35)
              }`,
              boxShadow: pageSuppliesOwnChrome
                ? "none"
                : theme.custom?.glassShadow ||
                  `0 8px 32px ${alpha(theme.palette.primary.dark, 0.15)}`,
              // Content layout
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              // Use contentText for proper contrast on glass surfaces
              color:
                theme.custom?.contentText?.primary ||
                theme.palette.text.primary,
              transition: "background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
              '&[data-empty-dashboard="true"]': {
                background: "transparent",
                border: "1px solid transparent",
                boxShadow: "none",
                backdropFilter: "none",
              },
              // Joan, 2026-05-27: same transparent treatment when the dashboard
              // hosts a single-focus form (V2 patient creation, NL query). The
              // form/result has its own visual containment; the outer glass
              // chrome would just leave a big empty area below it.
              '&[data-floating-content="true"]': {
                background: "transparent",
                border: "1px solid transparent",
                boxShadow: "none",
                backdropFilter: "none",
              },
            }}
          >
            {children}
          </Box>
        </Box>
      ) : isGlassFloatingPath ? (
        // ✅ Floating glass mode: translucent card floating over background
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
            mt: isMobile ? `${navbarHeight + 16}px` : `${navbarHeight + 16}px`, // push down below navbar
            mb: theme.spacing(2), // little bottom margin
            overflow: "auto", // 🔑 allow scrolling here
          }}
        >
          <FloatingGlassContainer
            sx={{
              width: isMobile ? "95%" : "70%",
            }}
          >
            {children}
          </FloatingGlassContainer>
        </Box>
      ) : (
        // ✅ Default / white/transparent background mode
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              width: isMobile || isWhiteBackgroundExcluded ? "100%" : "70%",
              backgroundColor: isWhiteBackgroundExcluded
                ? "transparent"
                : alpha(theme.palette.common.white, 0.85),
              boxShadow: isWhiteBackgroundExcluded
                ? "transparent"
                : `0px 8px 20px ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              minHeight: `calc(100vh)`,
              maxHeight: `calc(100vh)`,
              backdropFilter: "blur(10px)",
            }}
          >
            {children}
          </Box>
        </Box>
      )}

      {/* 🔹 Conditional footer / assistant */}
      {/* {!isFooterExcluded && <FloatingHelpAssistant />} */}

      {/* 🔹 Legal acceptance gate — shown BEFORE tutorial can start */}
      <LegalAcceptanceModal />

      {/* 🔹 First-login workspace setup (language + AI + region).
             Independent of the tutorial: dismissing it does NOT start one. */}
      <WelcomeSetupModal />

      {/* 🔹 Onboarding tutorial overlay — opened on demand, never on its own */}
      <OnboardingOverlay />
    </Box>
    </TutorialContext.Provider>
    </LegalComplianceContext.Provider>
  );
}
